// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { MainWindowManager } from '../UtilsClasses/MainWindowManager';
import TerceraBrokerMessageScreenTemplate from '@front/templates/TerceraBrokerMessageScreenTemplate.html';
import TerceraBrokerMessageScreenFooterTemplate from '@front/templates/TerceraBrokerMessageScreenFooterTemplate.html';
import { ScreensNames } from '../UtilsClasses/FactoryConstants';
import { TerceraWindowBase } from './TerceraWindowBase';
import { BrokerMessageResponseType } from '@shared/utils/Notifications/BrokerMessageResponseType';
import { UrlUtils } from '@shared/utils/UrlUtils';
import { ThemeManager } from '../misc/ThemeManager';
import { brokerMessageScreenHandler } from '@shared/utils/AppHandlers';
import { TextMessageType } from '@shared/utils/Notifications/TextMessageType';
import { DataCache } from '@shared/commons/DataCache';
import { Connection } from '@shared/commons/Connection';
export class TerceraBrokerMessageScreen extends TerceraWindowBase {
    constructor() {
        super(...arguments);
        this.CloseHandler = null;
        this.CustomButtonsXCloseHandler = null;
    }
    getType() {
        return ScreensNames.BrokerMessageScreen;
    }
    oninit() {
        super.oninit();
        this.on('onOKClick', this.onOKClick);
        this.on('onCancelClick', this.onCancelClick);
        this.on('onIframeLoad', this.onIframeLoad);
        this.observe('messageInfo', this.onMessageInfoChange);
    }
    onIframeLoad() {
        const elID = 'iframe_' + this.get('brokerMsgScreenID');
        const el = document.getElementById(elID);
        const messageInfo = this.get('messageInfo');
        const HTMLdisplayMethod = !isNullOrUndefined(messageInfo) ? messageInfo.IsHTMLDisplayMethod() : null; // #114121 цвет текста меняем для DisplayMethod == TextDisplayMethod.Text
        const changeIFrameTextColor = !isNullOrUndefined(el) && !HTMLdisplayMethod;
        if (changeIFrameTextColor) {
            /* пока не нашел css способа поменять цвет текста внутри iframe */
            el.contentWindow.document.body.style.color = ThemeManager.colors.SizeHistAllTextColor;
        }
    }
    oncomplete() {
        super.oncomplete();
        this.center();
    }
    localize() {
        const messageInfo = this.get('messageInfo');
        void this.set({
            header: isValidString(messageInfo === null || messageInfo === void 0 ? void 0 : messageInfo.Name) ? messageInfo.Name : Resources.getResource('BrokerMessage.Text'),
            noHtmlMessage: Resources.getResource('chart.NoData'),
            okButtonText: Resources.getResource('BrokerMessage.OK'),
            cancelButtonText: Resources.getResource('BrokerMessage.Cancel')
        });
    }
    onOKClick() {
        const msgInfo = this.get('messageInfo');
        if (msgInfo.TextMessageType === TextMessageType.BROKER_MESSAGE_URGENT_TYPE &&
            isNullOrUndefined(msgInfo.ResponseCases)) {
            msgInfo.Date = new Date();
            DataCache.SendBrokerMessageReport(msgInfo); // закоментовано, згодом відновлено згідно з #110904 (для urgent BM було додано логіювання у #84587, судячи з усього зараз ця логіка вже є застарілою і непотрібною)
        }
        this.processResponseCasesIfNeed(); // #92056 send answer if msg has cases
        this.close();
    }
    onCancelClick() {
        this.close();
        Connection.disconnect();
    }
    // Close button (cross icon).
    onCloseButtonClick() {
        if (this.canCloseFromButton()) {
            this.close(true);
        }
    }
    close(fromCloseBTN = false) {
        super.close();
        this.processNotificationWasRead();
        if (!isNullOrUndefined(this.CloseHandler)) {
            this.CloseHandler();
        }
        if (!isNullOrUndefined(this.CustomButtonsXCloseHandler) && fromCloseBTN) {
            this.CustomButtonsXCloseHandler();
        }
    }
    onMessageInfoChange(messageInfo) {
        if (isNullOrUndefined(messageInfo)) {
            return;
        }
        void this.set('style_addition_footer', '');
        const msgType = messageInfo.TextMessageType;
        if (msgType === TextMessageType.BROKER_MESSAGE_URGENT_TYPE ||
            msgType === TextMessageType.BROKER_MESSAGE_DAILY_WELCOME_TYPE ||
            msgType === TextMessageType.BROKER_MESSAGE_NOTIFICATION_TYPE) {
            /* если в (51) месседже не передано доп инфо ни для одной кнопки, то логика будет текущей, просто показываем стандартный брокер месседж с кнопкой ОК */
            void this.set({
                cancelButtonVisible: false
            });
        }
        if (msgType === TextMessageType.BROKER_MESSAGE_NOTIFICATION_TYPE) {
            const cBtns = messageInfo.CustomButtons;
            if ((cBtns === null || cBtns === void 0 ? void 0 : cBtns.length) > 0) {
                const fH = 2 * TerceraBrokerMessageScreen.BTN_VERTICAL_OFFSET +
                    cBtns.length * (TerceraBrokerMessageScreen.BTN_HEIGHT + TerceraBrokerMessageScreen.BTN_MARGIN);
                void this.set({
                    okButtonVisible: false,
                    closeBtnVisible: true,
                    footer_height: fH,
                    style_addition_footer: 'js-TerceraBrokerMessageScreen-AdditionalFooter',
                    customButtons: cBtns
                });
                this.createCustomButtonsClickEvents();
                this.CustomButtonsXCloseHandler = () => {
                    this.processResponseCustomButtons(TerceraBrokerMessageScreen.BM_CLOSED);
                };
            }
        }
    }
    createCustomButtonsClickEvents() {
        const clickEvent = (url, customBtnTitle) => {
            UrlUtils.openFixedURL(url);
            this.processResponseCustomButtons(customBtnTitle);
            this.close();
        };
        const cbs = this.get('customButtons');
        for (let i = 0; i < cbs.length; i++) {
            this.on('onCustomButton' + i + 'Click', clickEvent.bind(this, cbs[i].URL, cbs[i].TitleForResponse));
        }
    }
    processResponseCasesIfNeed() {
        var _a, _b;
        const msgInfo = this.get('messageInfo');
        if (isNullOrUndefined(msgInfo === null || msgInfo === void 0 ? void 0 : msgInfo.ResponseCases))
            return;
        const { ID: brokerMsgID, ClusterNode: clusterNode } = msgInfo;
        const userID = +DataCache.getUserID();
        const userName = DataCache.getUserName();
        const screenID = TerceraBrokerMessageScreen.ID_CONST_PREFIX + brokerMsgID;
        const iframe = (_a = document.getElementById(screenID)) === null || _a === void 0 ? void 0 : _a.firstChild;
        const htmlContent = (_b = iframe === null || iframe === void 0 ? void 0 : iframe.contentWindow) === null || _b === void 0 ? void 0 : _b.document;
        if (isNullOrUndefined(htmlContent))
            return;
        const inputArr = Array.from(htmlContent.getElementsByTagName('input'));
        for (const input of inputArr) {
            if (input.checked) {
                const value = input.getAttribute('value') === 'yes' ? 'true' : 'false';
                DataCache.SendBrokerResponseMessage({
                    brokerMsgID,
                    value,
                    userID,
                    userName,
                    responseType: BrokerMessageResponseType.RADIO_BTNS,
                    clusterNode
                });
                return;
            }
        }
    }
    /* https://tp.traderevolution.com/entity/109652 send BrokerResponseMessage when custom buttons clicked */
    processResponseCustomButtons(cBtnTitle) {
        const msgInfo = this.get('messageInfo');
        if (isNullOrUndefined(msgInfo))
            return;
        const responseParams = {
            brokerMsgID: msgInfo.ID,
            value: cBtnTitle.toString(),
            userID: +DataCache.getUserID(),
            userName: DataCache.getUserName(),
            responseType: BrokerMessageResponseType.CUSTOM_BTNS,
            clusterNode: msgInfo.ClusterNode
        };
        DataCache.SendBrokerResponseMessage(responseParams);
    }
    processNotificationWasRead() {
        const msgInfo = this.get('messageInfo');
        if (isNullOrUndefined(msgInfo) ||
            msgInfo.TextMessageType !== TextMessageType.BROKER_MESSAGE_NOTIFICATION_TYPE ||
            msgInfo.ShowPopUp === false) {
            return;
        }
        const responseParams = {
            brokerMsgID: msgInfo.ID,
            value: '',
            userID: +DataCache.getUserID(),
            userName: DataCache.getUserName(),
            responseType: BrokerMessageResponseType.WAS_READ,
            clusterNode: msgInfo.ClusterNode
        };
        DataCache.SendBrokerResponseMessage(responseParams);
    }
    static Show(messageInfo) {
        if (isNullOrUndefined(messageInfo)) {
            return;
        }
        if (!MainWindowManager.IsWorkMode /*||*/ /* <- Reinforce check due to recurring issue #126810 */
        /*!SessionSettings.OnBoardingWasShown()*/
        ) {
            /* #110922 если нужно показать OnBoarding то BrokerMessage покажем после его завершения */
            TerceraBrokerMessageScreen.delayedMessages.push(messageInfo);
            return;
        }
        const scr = new TerceraBrokerMessageScreen();
        void scr.set({
            messageInfo,
            brokerMsgScreenID: !isNullOrUndefined(messageInfo)
                ? TerceraBrokerMessageScreen.ID_CONST_PREFIX + messageInfo.ID
                : null
        });
        MainWindowManager.AddControl(scr);
        scr.setFocus();
        scr.AsyncClosePromise = new Promise((resolve) => {
            scr.CloseHandler = resolve;
        });
        MainWindowManager.AsyncCloseObject = scr;
    }
    static ShowDelayedMessages() {
        for (const message of TerceraBrokerMessageScreen.delayedMessages) {
            TerceraBrokerMessageScreen.Show(message);
        }
        TerceraBrokerMessageScreen.delayedMessages = [];
    }
    static brokerMessageScreenHandlerInitialize() {
        brokerMessageScreenHandler.Show = TerceraBrokerMessageScreen.Show;
    }
}
TerceraBrokerMessageScreen.delayedMessages = [];
TerceraBrokerMessageScreen.ID_CONST_PREFIX = 'BrokerScreenID=';
TerceraBrokerMessageScreen.BM_CLOSED = 'BM closed';
TerceraBrokerMessageScreen.BTN_HEIGHT = 32;
TerceraBrokerMessageScreen.BTN_VERTICAL_OFFSET = 16;
TerceraBrokerMessageScreen.BTN_MARGIN = 8;
TerceraBrokerMessageScreen.HEADER = 52;
TerceraWindowBase.extendWith(TerceraBrokerMessageScreen, {
    data: function () {
        return {
            style_addition_footer: '',
            width: 500,
            height: 300,
            minWidth: 300,
            minHeight: 300,
            showFooter: true,
            showModalMask: true,
            resizable: true,
            brokerMsgScreenID: null,
            messageInfo: null,
            noHtmlMessage: '',
            customButtonWidth: 280,
            customButtons: [],
            okButtonVisible: true,
            cancelButtonVisible: true,
            closeBtnVisible: false
        };
    },
    partials: {
        bodyPartial: TerceraBrokerMessageScreenTemplate,
        footerPartial: TerceraBrokerMessageScreenFooterTemplate
    }
});
